import {
  DefaultTheme,
  ThemeProvider as StyledComponentsThemeProvider,
  StyleSheetManager,
} from "styled-components";

import { createPrimaryColors } from "../styles";
import { ClassPrefixContext, DEFAULT_CLASS_PREFIX } from "./classPrefix";
import { theme } from "./consts";
import { GlobalStyles } from "./GlobalStyles";

interface ThemeProviderProps {
  /** Primary color for the theme */
  primaryColor: string;
  /** Inject styled-components stylesheet inside this HTML element.
   *
   * Useful for injecting styles into a Shadow DOM. */
  styleSheetTarget?: HTMLElement;
  /**
   * Prefix for shadow-DOM class names and inner ids (`${prefix}-chat-header`).
   * Defaults to `"tomis"` so existing `tomis-chat-*` strings stay unchanged.
   */
  classPrefix?: string;
}

/** Theme provider for all child components.
 * Includes the TOMIS default theme (colors, shadows, spacing, etc.),
 * and allows for customization of the primary color.
 *
 * ## Example Usage
 * ```tsx
 * import { ThemeProvider } from "@tomis-tech/chat-ui";
 *
 * <ThemeProvider primaryColor="#44aa66" classPrefix="yonder">
 *    <App />
 * </ThemeProvider>
 * ```
 * In child components, access the theme via the `useTheme` hook.
 * ```tsx
 * import { useTheme } from "@tomis-tech/chat-ui";
 *
 * const MyComponent: React.FC = () => {
 *   const theme = useTheme();
 *   return <div style={{ backgroundColor: theme.colors.primary }} />;
 * };
 * ```
 */
export const ThemeProvider: React.FC<
  React.PropsWithChildren<ThemeProviderProps>
> = ({
  children,
  primaryColor = "#309F87",
  styleSheetTarget,
  classPrefix = DEFAULT_CLASS_PREFIX,
}) => {
  const customizedTheme: DefaultTheme = {
    ...theme,
    colors: {
      ...theme.colors,
      ...createPrimaryColors(primaryColor),
    },
  };

  const themed = styleSheetTarget ? (
    <StyleSheetManager target={styleSheetTarget}>
      <GlobalStyles />
      <StyledComponentsThemeProvider theme={customizedTheme}>
        {children}
      </StyledComponentsThemeProvider>
    </StyleSheetManager>
  ) : (
    <>
      <GlobalStyles />
      <StyledComponentsThemeProvider theme={customizedTheme}>
        {children}
      </StyledComponentsThemeProvider>
    </>
  );

  return (
    <ClassPrefixContext.Provider value={classPrefix}>
      {themed}
    </ClassPrefixContext.Provider>
  );
};
